import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { range } from 'lit/directives/range.js'
import { CSEPaginationButton } from './pagination.js'


export class PaginationOnNavigateEvent extends Event {
  static type = 'pagination-on-navigate' as const
  pageNumber: number

  constructor(pageNumber: number) {
    super(PaginationOnNavigateEvent.type, { bubbles: true, composed: true })
    this.pageNumber = pageNumber
  }
}

@customElement('cse-pagination')
export class CSEPaginationSection extends LitElement {
  @property({ type: Number, attribute: 'current-page' })
  currentPage: number = 1

  @property({ type: Number, attribute: 'number-of-pages' })
  numberOfPages?: number

  @property({ type: Number, attribute: 'surrounding-pages' })
  surroundingPages?: number

  @property(({ type: Boolean, attribute: 'squash' }))
  squash: boolean = false

  private onNextClick = (event: Event) => {
    const target = event.target as CSEPaginationButton
    this.gotoPage(this.currentPage + 1, target)
  }

  private onPreviousClick = (event: Event) => {
    const target = event.target as CSEPaginationButton
    this.gotoPage(this.currentPage - 1, target)
  }

  private gotoPage(pageNumber: number, target: CSEPaginationButton) {
    if (target.disabled) {
      return
    }
    this.dispatchEvent(new PaginationOnNavigateEvent(pageNumber))
    this.currentPage = pageNumber
  }

  private doSquashLeft(first: number, last: number) {
    const numberOfRenderedSurroundingPages = this.surroundingPages || 3
    if (last - first > numberOfRenderedSurroundingPages) {
      const numberOfFlexibleElements = numberOfRenderedSurroundingPages - 2
      return html`
        <cse-pagination-button @click="${(event: any) => this.gotoPage(1, event.target)}">${first}</cse-pagination-button>
        <cse-pagination-button disabled>...</cse-pagination-button>
        ${this.renderPageRange(last - numberOfFlexibleElements, last)}
      `
    }
    return this.renderPageRange(first, last)
  }

  private doSquashRight(first: number, last: number) {
    const numberOfRenderedSurroundingPages = this.surroundingPages || 3
    if (last - first > numberOfRenderedSurroundingPages) {
      const numberOfFlexibleElements = numberOfRenderedSurroundingPages - 2
      return html`
        ${this.renderPageRange(first, first + numberOfFlexibleElements)}
        <cse-pagination-button disabled>...</cse-pagination-button>
        <cse-pagination-button @click="${(event: any) => this.gotoPage(last - 1, event.target)}">${last - 1}</cse-pagination-button>
      `
    }
    return this.renderPageRange(first, last)
  }

  private leftSurroundingPages() {
    let range_start: number = 1
    const range_end: number = this.currentPage

    if (this.surroundingPages !== undefined) {
      range_start = this.currentPage - this.surroundingPages
    }

    if (this.squash) {
      return this.doSquashLeft(1, range_end)
    }
    return this.renderPageRange(range_start, range_end)
  }

  private rightSurroundingPages() {
    const range_start: number = this.currentPage + 1
    let range_end: number = (this.numberOfPages || this.currentPage) + 1

    if (this.surroundingPages !== undefined) {
      range_end = range_start + this.surroundingPages
    }

    if (this.squash && this.numberOfPages) {
      return this.doSquashRight(range_start, this.numberOfPages + 1)
    }
    return this.renderPageRange(range_start, range_end)
  }

  private renderPageRange(range_start: number, range_end: number) {
    return html`
      ${map(range(range_start, range_end), (page: number) => {
        return html`<cse-pagination-button @click="${(event: any) => this.gotoPage(page, event.target)}">${page}</cse-pagination-button>`
      })}
    `
  }

  override render() {
    const last_known_page = this.numberOfPages !== undefined && this.currentPage >= this.numberOfPages
    return html`
      <cse-group justify="center">
        <cse-pagination-button previous ?disabled=${this.currentPage === 1} @click=${this.onPreviousClick}></cse-pagination-button>
      
        ${this.leftSurroundingPages()}
        
        <cse-pagination-button active>${this.currentPage}</cse-pagination-button>
        
        ${this.rightSurroundingPages()}
        
        <cse-pagination-button next ?disabled=${last_known_page} @click="${this.onNextClick}"></cse-pagination-button>
      </cse-group>
    `
  }
}
